import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { WORD_BUILDER_ITEMS } from '../../core/curriculum/curriculumData';
import { speechProvider } from '../../core/speech/speechProvider';
import { appRepository } from '../../core/storage/repository';
import { RaraMascot, MascotState } from '../../components/mascot/RaraMascot';
import { CelebrationStars } from './components/CelebrationStars';
import { BackIcon, PuzzleIcon, SpeakerIcon } from '../../components/icons/ReadingIcons';
import { seededShuffle } from '../../core/games/gameRules';
import confetti from 'canvas-confetti';

interface WordBuilderViewProps {
  onBack: () => void;
}

export const WordBuilderView: React.FC<WordBuilderViewProps> = ({ onBack }) => {
  const [wordOrder] = useState(() => seededShuffle(WORD_BUILDER_ITEMS, Date.now()));
  const [currentIndex, setCurrentIndex] = useState(0);
  const currentItem = wordOrder[currentIndex];

  // Placed syllable slots
  const [placedSlots, setPlacedSlots] = useState<string[]>([]);
  // Available scattered choices (shuffled)
  const [availableBlocks, setAvailableBlocks] = useState<string[]>(() =>
    seededShuffle(currentItem.syllables, Date.now())
  );
  const [isSuccess, setIsSuccess] = useState(false);
  const [mascotMessage, setMascotMessage] = useState('Susun suku kata agar menjadi kata yang tepat!');
  const [mascotState, setMascotState] = useState<MascotState>('idle');
  const [isPlayingSentence, setIsPlayingSentence] = useState(false);
  const [highlightedWordIndex, setHighlightedWordIndex] = useState<number | null>(null);

  // Efek Perayaan Kembang Api / Confetti Bertingkat
  const triggerCelebration = () => {
    speechProvider.playChime('celebrate');

    // Ledakan confetti tengah (aneka warna & bintang)
    confetti({
      particleCount: 60,
      spread: 80,
      origin: { y: 0.55 },
      colors: ['#F59E0B', '#EF4444', '#10B981', '#3B82F6', '#8B5CF6', '#FBBF24'],
    });

    // Meriam confetti sisi kiri
    setTimeout(() => {
      confetti({
        particleCount: 35,
        angle: 60,
        spread: 60,
        origin: { x: 0.1, y: 0.65 },
        colors: ['#FBBF24', '#34D399', '#60A5FA', '#F472B6'],
      });
    }, 250);

    // Meriam confetti sisi kanan
    setTimeout(() => {
      confetti({
        particleCount: 35,
        angle: 120,
        spread: 60,
        origin: { x: 0.9, y: 0.65 },
        colors: ['#F472B6', '#FBBF24', '#A78BFA', '#34D399'],
      });
    }, 500);
  };

  const handleSelectBlock = (block: string, index: number) => {
    if (isSuccess) return;

    // Check if block matches next expected slot
    const nextSlotIndex = placedSlots.length;
    const expectedSyllable = currentItem.syllables[nextSlotIndex];

    if (block === expectedSyllable) {
      // Correct block!
      const newPlaced = [...placedSlots, block];
      setPlacedSlots(newPlaced);
      const newAvailable = availableBlocks.filter((_, idx) => idx !== index);
      setAvailableBlocks(newAvailable);

      speechProvider.playChime('tap');
      speechProvider.speak(block.toLowerCase(), { rate: 0.85 });

      if (newPlaced.length === currentItem.syllables.length) {
        // Complete word assembled!
        handleWordCompleted();
      } else {
        setMascotState('happy');
        setMascotMessage('Benar! Sekarang pilih suku kata berikutnya!');
      }
    } else {
      // Wrong block chosen
      speechProvider.playChime('tap');
      setMascotState('encourage');
      setMascotMessage('Hampir! Coba dengar dulu bunyi suku katanya, yuk!');
      speechProvider.speak(block.toLowerCase(), { rate: 0.8 });
    }
  };

  const handleWordCompleted = async () => {
    setIsSuccess(true);
    triggerCelebration();
    setMascotState('celebrate');
    setMascotMessage(`Yeay, hebat! "${currentItem.word}" tersusun! Sentuh contoh kalimatnya di bawah untuk bersuara!`);

    appRepository.recordWordMastered(currentItem.word);

    // Pronounce syllables and full word
    await speechProvider.speakSyllablesAndWord(
      currentItem.syllables.map(s => s.toLowerCase()),
      currentItem.word
    );
  };

  // Melafalkan seluruh contoh kalimat (e.g. "Ibu makan roti.")
  const handlePlaySentence = async (sentence: string) => {
    if (isPlayingSentence) return;
    setIsPlayingSentence(true);
    setHighlightedWordIndex(null);
    setMascotState('speaking');
    setMascotMessage(`"${sentence}"`);
    speechProvider.playChime('tap');
    await speechProvider.speak(sentence, { rate: 0.88 });
    setIsPlayingSentence(false);
    setMascotState('happy');
  };

  // Melafalkan kata tunggal dalam kalimat (e.g. "Ibu", "makan", "roti")
  const handlePlaySingleWord = async (word: string, index: number) => {
    setHighlightedWordIndex(index);
    setMascotState('speaking');
    setMascotMessage(`Kata: ${word}`);
    speechProvider.playChime('tap');
    await speechProvider.speak(word, { rate: 0.88 });
    setTimeout(() => {
      setHighlightedWordIndex(null);
      setMascotState('happy');
    }, 1000);
  };

  // Melafalkan kata yang sudah tersusun saja (e.g. "ROTI")
  const handlePlayAssembledWord = async () => {
    setMascotState('speaking');
    setMascotMessage(`Kata: ${currentItem.word}`);
    speechProvider.playChime('tap');
    await speechProvider.speak(currentItem.word, { rate: 0.88 });
    setMascotState('happy');
  };

  const handleNextWord = () => {
    const nextIdx = (currentIndex + 1) % wordOrder.length;
    const nextItem = wordOrder[nextIdx];
    setCurrentIndex(nextIdx);
    setPlacedSlots([]);
    setAvailableBlocks(seededShuffle(nextItem.syllables, Date.now()));
    setIsSuccess(false);
    setIsPlayingSentence(false);
    setHighlightedWordIndex(null);
    setMascotState('idle');
    setMascotMessage('Ayo susun kata baru!');
  };

  const handleResetCurrent = () => {
    setPlacedSlots([]);
    setAvailableBlocks(seededShuffle(currentItem.syllables, Date.now()));
    setIsSuccess(false);
    setIsPlayingSentence(false);
    setHighlightedWordIndex(null);
    setMascotState('idle');
    setMascotMessage('Yuk coba susun lagi!');
  };

  return (
    <div className="flex flex-col min-h-screen bg-[#FFFDF8] pb-12 select-none">
      {/* Top Header */}
      <div className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b-2 border-amber-100 px-4 py-3 flex items-center justify-between">
        <button
          onClick={onBack}
          className="flex items-center gap-2 bg-amber-100 hover:bg-amber-200 active:scale-95 text-amber-900 font-bold px-4 py-2.5 rounded-2xl transition cursor-pointer"
        >
          <BackIcon className="w-6 h-6" />
          <span className="text-sm md:text-base">Kembali</span>
        </button>

        <h1 className="text-xl md:text-2xl font-display font-bold text-[#4F7CF7] flex items-center gap-2">
          <PuzzleIcon className="w-9 h-9" /> Susun Kata
        </h1>

        <div className="flex items-center gap-1.5 bg-amber-100 text-amber-900 px-3 py-1.5 rounded-2xl font-bold text-sm">
          <span>Kata {currentIndex + 1} / {WORD_BUILDER_ITEMS.length}</span>
        </div>
      </div>

      <div className="max-w-3xl mx-auto w-full px-4 pt-4 flex-1 flex flex-col gap-5">
        {/* Mascot companion with clickable bubble */}
        <RaraMascot
          state={mascotState}
          message={mascotMessage}
          size="sm"
          onBubbleClick={() => {
            if (isSuccess) {
              handlePlaySentence(currentItem.sentenceExample);
            }
          }}
        />

        {/* Puzzle Board Card with Relative Anchor for Celebration Stars */}
        <div className="relative bg-white rounded-3xl p-6 md:p-8 shadow-xl border-4 border-blue-200 flex flex-col items-center justify-center text-center overflow-visible">
          {/* Framer Motion Celebration Stars & Confetti Aura */}
          <CelebrationStars active={isSuccess} onReplay={triggerCelebration} />

          {/* Target Clue Emoji / Image */}
          <motion.div
            animate={
              isSuccess
                ? {
                    scale: [1, 1.15, 1],
                    rotate: [0, -6, 6, 0],
                  }
                : { scale: 1, rotate: 0 }
            }
            transition={
              isSuccess
                ? { duration: 0.8, repeat: Infinity, repeatDelay: 3, ease: 'easeInOut' }
                : { duration: 0.2 }
            }
            className="w-24 h-24 md:w-32 md:h-32 bg-blue-50 rounded-3xl border-3 border-blue-200 flex items-center justify-center text-5xl md:text-6xl mb-3 shadow-inner z-10"
          >
            {currentItem.emoji}
          </motion.div>

          <div className="text-xs font-bold text-[#64748B] uppercase tracking-wider mb-1 z-10">
            Kategori: {currentItem.category}
          </div>

          {/* Target Slots Display with Safe Keyframe Transitions */}
          <div className="flex items-center justify-center gap-3 md:gap-4 my-5 z-10">
            {currentItem.syllables.map((syl, idx) => {
              const isFilled = placedSlots[idx] !== undefined;
              return (
                <motion.div
                  key={`slot-${idx}-${syl}`}
                  initial={false}
                  animate={
                    isSuccess
                      ? {
                          scale: [1, 1.15, 1],
                          y: [0, -14, 0],
                        }
                      : isFilled
                      ? { scale: 1, rotate: 0 }
                      : { scale: 1, rotate: 0 }
                  }
                  transition={
                    isSuccess
                      ? {
                          duration: 0.6,
                          delay: idx * 0.12,
                          ease: 'easeInOut',
                          repeat: 1,
                          repeatDelay: 1.8,
                        }
                      : isFilled
                      ? { type: 'spring', stiffness: 350, damping: 15 }
                      : undefined
                  }
                  className={`w-24 h-24 md:w-28 md:h-28 rounded-3xl border-4 flex items-center justify-center font-display font-black text-3xl md:text-4xl shadow-md transition-colors ${
                    isSuccess
                      ? 'bg-linear-to-b from-amber-400 to-yellow-400 text-amber-950 border-amber-500 shadow-xl ring-4 ring-amber-200'
                      : isFilled
                      ? 'bg-blue-600 text-white border-blue-700 shadow-lg'
                      : 'bg-blue-50/60 border-dashed border-blue-300 text-transparent'
                  }`}
                >
                  {isFilled ? placedSlots[idx] : '?'}
                </motion.div>
              );
            })}
          </div>

          {/* Assembled Word & Interactive Sentence Card */}
          <AnimatePresence>
            {isSuccess && (
              <motion.div
                key="assembled-word-container"
                initial={{ opacity: 0, y: 25, scale: 0.92 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ type: 'spring', stiffness: 320, damping: 20 }}
                className="w-full flex flex-col items-center gap-4 my-2 z-10"
              >
                {/* Assembled Word Display with Sound Button */}
                <div className="flex flex-col items-center">
                  <span className="text-4xl md:text-5xl font-display font-black text-[#243047] tracking-wide">
                    {currentItem.word}
                  </span>
                  <button
                    onClick={handlePlayAssembledWord}
                    className="mt-2 flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-blue-100 hover:bg-blue-200 active:scale-95 text-blue-900 font-bold text-xs shadow-2xs transition cursor-pointer"
                    title="Sentuh untuk bunyikan kata saja"
                  >
                    <SpeakerIcon className="w-6 h-6" />
                    <span>Bunyikan Kata "{currentItem.word}"</span>
                  </button>
                </div>

                {/* Interactive Sentence Card (Klik untuk dilafalkan, misal "Ibu makan roti.") */}
                <div
                  className={`w-full max-w-lg rounded-3xl border-3 p-4 md:p-5 transition-all shadow-md text-left ${
                    isPlayingSentence
                      ? 'bg-amber-100 border-amber-500 ring-4 ring-amber-200 scale-101'
                      : 'bg-amber-50/90 border-amber-300 hover:border-amber-400'
                  }`}
                >
                  {/* Header Card Contoh Kalimat */}
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <div className="flex items-center gap-1.5 text-xs font-extrabold text-amber-900 uppercase tracking-wider">
                      <span className="text-base">💬</span>
                      <span>Contoh Kalimat (Sentuh untuk Suara):</span>
                    </div>
                    <button
                      onClick={() => handlePlaySentence(currentItem.sentenceExample)}
                      className={`flex items-center gap-1 text-xs font-black px-3.5 py-1.5 rounded-xl shadow-xs transition active:scale-95 cursor-pointer shrink-0 ${
                        isPlayingSentence
                          ? 'bg-amber-600 text-white animate-pulse'
                          : 'bg-amber-500 hover:bg-amber-600 text-white'
                      }`}
                      title="Sentuh untuk mendengarkan seluruh kalimat"
                    >
                      <SpeakerIcon className="w-5 h-5" />
                      <span>{isPlayingSentence ? 'Membaca...' : 'Dengar Kalimat ▶'}</span>
                    </button>
                  </div>

                  {/* Interactive Word Chips: Sentuh seluruh kalimat atau sentuh per kata */}
                  <div
                    onClick={() => handlePlaySentence(currentItem.sentenceExample)}
                    className="bg-white/90 border-2 border-amber-200 hover:border-amber-300 rounded-2xl p-3 md:p-4 flex flex-wrap items-center gap-2 cursor-pointer transition shadow-2xs"
                    title="Sentuh kotak ini untuk mendengarkan seluruh kalimat"
                  >
                    {currentItem.sentenceExample.split(' ').map((w, idx) => {
                      const cleanWord = w.replace(/[.,!?;:]/g, '');
                      const isHighlighted = highlightedWordIndex === idx;
                      return (
                        <button
                          key={`sentence-word-${idx}-${cleanWord}`}
                          onClick={(e) => {
                            e.stopPropagation(); // Biarkan fokus membaca kata yang disentuh
                            handlePlaySingleWord(cleanWord, idx);
                          }}
                          className={`px-3 py-1.5 rounded-xl font-display font-black text-lg md:text-xl border-2 transition active:scale-90 cursor-pointer ${
                            isHighlighted
                              ? 'bg-amber-500 text-white border-amber-600 scale-110 shadow-md ring-2 ring-amber-300'
                              : 'bg-amber-50 hover:bg-amber-200/80 text-[#243047] border-amber-200'
                          }`}
                          title={`Sentuh untuk melafalkan kata "${cleanWord}"`}
                        >
                          {w}
                        </button>
                      );
                    })}
                  </div>

                  {/* Sub-keterangan petunjuk anak & orang tua */}
                  <div className="flex items-center justify-between text-[11px] font-semibold text-amber-800/90 mt-2.5 px-1">
                    <span>👉 Sentuh kata per kata atau tombol oranye</span>
                    <button
                      onClick={() => handlePlaySentence(currentItem.sentenceExample)}
                      className="underline hover:text-amber-950 font-bold cursor-pointer"
                    >
                      <><SpeakerIcon className="w-5 h-5 inline-block align-middle" /> Lafalkan Semua</>
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Available Syllable Choice Blocks (Ketika kata belum selesai) */}
          {!isSuccess ? (
            <div className="mt-4 w-full z-10">
              <div className="text-xs font-bold text-[#64748B] mb-3">
                Sentuh suku kata di bawah sesuai urutan:
              </div>
              <div className="flex flex-wrap items-center justify-center gap-4">
                {availableBlocks.map((block, idx) => (
                  <motion.button
                    key={`choice-block-${idx}-${block}`}
                    whileHover={{ scale: 1.06 }}
                    whileTap={{ scale: 0.92 }}
                    onClick={() => handleSelectBlock(block, idx)}
                    className="w-24 h-24 md:w-28 md:h-28 rounded-3xl bg-amber-400 hover:bg-amber-500 border-4 border-amber-500 text-amber-950 font-display font-black text-3xl md:text-4xl shadow-lg transition-all flex items-center justify-center cursor-pointer"
                  >
                    {block}
                  </motion.button>
                ))}
              </div>
            </div>
          ) : (
            /* Action Buttons (Setelah kata berhasil disusun) */
            <div className="flex flex-wrap items-center justify-center gap-3 mt-6 z-10">
              <button
                onClick={() =>
                  speechProvider.speakSyllablesAndWord(
                    currentItem.syllables.map(s => s.toLowerCase()),
                    currentItem.word
                  )
                }
                className="px-5 py-3 bg-blue-500 hover:bg-blue-600 text-white font-bold rounded-2xl shadow active:scale-95 flex items-center gap-2 text-sm md:text-base cursor-pointer"
              >
                <SpeakerIcon className="w-6 h-6" />
                <span>Suku Kata & Kata</span>
              </button>

              <button
                onClick={() => handlePlaySentence(currentItem.sentenceExample)}
                className="px-5 py-3 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-2xl shadow active:scale-95 flex items-center gap-2 text-sm md:text-base cursor-pointer"
              >
                <span>💬</span>
                <span>Contoh Kalimat</span>
              </button>

              {/* Tombol Rayakan Lagi untuk mengulang efek confetti & bintang */}
              <button
                onClick={triggerCelebration}
                className="px-5 py-3 bg-linear-to-r from-amber-400 to-yellow-400 hover:from-amber-500 hover:to-yellow-500 text-amber-950 font-black rounded-2xl shadow active:scale-95 flex items-center gap-2 text-sm md:text-base cursor-pointer"
                title="Putar ulang animasi bintang & confetti"
              >
                <span>✨</span>
                <span>Rayakan Lagi!</span>
              </button>

              <button
                onClick={handleNextWord}
                className="px-6 py-3 bg-[#4BC27A] hover:bg-green-600 text-white font-black rounded-2xl shadow-lg active:scale-95 flex items-center gap-2 text-sm md:text-base cursor-pointer"
              >
                <span>Kata Berikutnya</span>
                <span>➡️</span>
              </button>
            </div>
          )}

          {!isSuccess && placedSlots.length > 0 && (
            <button
              onClick={handleResetCurrent}
              className="mt-6 text-xs text-gray-500 hover:text-gray-700 underline font-semibold cursor-pointer z-10"
            >
              Ulangi susunan kata ini
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
